import { Component, Input, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { NbDialogRef, NbToastrService } from "@nebular/theme";
import { CompanyUser } from "src/app/models/company-user.model";
import { UserCompanyRole } from "src/app/models/user-company.model";
import { UserManagementContext } from "../user-list/user-list.component";
import { UserService } from "src/app/services/user.service";
import { UtilityService } from "src/app/services/utility.service";
import { AuthService } from "src/app/services/auth.service";
import { CompanyService } from "src/app/services/company.service";
import { ANIMAL_TYPES } from "src/app/constants/animal-types.constants";

@Component({
    selector: "app-user-edit-dialog",
    templateUrl: "./user-edit-dialog.component.html",
    styleUrls: ["./user-edit-dialog.component.scss"],
    standalone: false
})
export class UserEditDialogComponent implements OnInit {
  @Input() context: UserManagementContext;
  @Input() companyId: number;
  @Input() user?: CompanyUser;

  public loading = false;
  public checkingEmail = false;
  public emailExists = false;
  public emailChecked = false;
  public isQwintalAdmin = false;
  public editForm: UntypedFormGroup;
  public availableRoles: string[] = [];
  public externalRoles: string[] = [];
  public isMultiCompanyUser = false;
  public errorMessage: string | null = null;
  public availableAnimalTypes: { id: number; label: string }[] = [];
  public loadingAnimalTypes = false;

  constructor(
    protected dialogRef: NbDialogRef<UserEditDialogComponent>,
    protected formBuilder: UntypedFormBuilder,
    protected userService: UserService,
    protected utilityService: UtilityService,
    public authService: AuthService,
    protected toastrService: NbToastrService,
    protected companyService: CompanyService,
  ) {}

  ngOnInit(): void {
    this.loadRoles();
    // Le formulaire doit exister avant le chargement des espèces, qui y présélectionne des valeurs
    this.initForm();
    this.loadAvailableAnimalTypes();
  }

  private loadRoles(): void {
    this.utilityService.getRoles().subscribe((roles) => {
      this.availableRoles = roles;
      // External roles are all except breeder and breederadmin
      this.externalRoles = roles.filter((r) => r !== "breeder" && r !== "breederadmin");
    });
  }

  private loadAvailableAnimalTypes(): void {
    if (!this.companyId) {
      this.errorMessage = "ID de l'entreprise manquant";
      return;
    }

    this.loadingAnimalTypes = true;

    // Récupérer les types d'animaux disponibles pour le user connecté dans cette company
    const currentUser = this.authService.getUser();
    const currentUserCompany = currentUser?.companies?.find(c => c.id === this.companyId);

    if (currentUserCompany && currentUserCompany.animalTypeIds) {
      this.availableAnimalTypes = currentUserCompany.animalTypeIds.map((typeId) => ({
        id: typeId,
        label: ANIMAL_TYPES.find((at) => at.id === typeId)?.label || `Type ${typeId}`,
      }));
      this.setDefaultAnimalTypes();
      this.loadingAnimalTypes = false;
    } else {
      // Si le user n'a pas de company (cas admin Qwintal), charger tous les types de la company
      this.companyService.getById(this.companyId).subscribe(
        (company) => {
          this.loadingAnimalTypes = false;
          if (company && company.animalTypeIds) {
            this.availableAnimalTypes = company.animalTypeIds.map((typeId) => ({
              id: typeId,
              label: ANIMAL_TYPES.find((at) => at.id === typeId)?.label || `Type ${typeId}`,
            }));
            this.setDefaultAnimalTypes();
          }
        },
        (error) => {
          this.loadingAnimalTypes = false;
          this.errorMessage = "Erreur lors du chargement des types d'animaux";
        },
      );
    }
  }

  private setDefaultAnimalTypes(): void {
    if (!this.user && this.editForm) {
      // Pré-sélectionner TOUS les types pour les nouveaux utilisateurs
      const allTypeIds = this.availableAnimalTypes.map((at) => at.id);
      this.editForm.patchValue({ animalTypeIds: allTypeIds });
    }
  }

  private atLeastOneAnimalTypeValidator() {
    return (control: any) => {
      const value = control.value;
      if (!value || !Array.isArray(value) || value.length === 0) {
        return { atLeastOneRequired: true };
      }
      return null;
    };
  }

  private initForm(): void {
    const isEditMode = !!this.user;

    this.editForm = this.formBuilder.group({
      email: [
        { value: this.user?.email || null, disabled: isEditMode },
        [Validators.required, Validators.email],
      ],
      firstname: [
        { value: this.user?.firstname || null, disabled: !isEditMode },
        Validators.required,
      ],
      lastname: [
        { value: this.user?.lastname || null, disabled: !isEditMode },
        Validators.required,
      ],
      phone: [{ value: this.user?.phone || null, disabled: !isEditMode }],
      // For admin context
      isBreederAdmin: [{ value: this.user?.role === "breederadmin", disabled: !isEditMode }],
      // For breederadmin context
      isExternal: [{ value: this.user?.isExternalRole() || false, disabled: !isEditMode }],
      externalRole: [this.user?.isExternalRole() ? this.user.role : null],
      // Animal types
      animalTypeIds: [
        this.user?.animalTypeIds || [],
        [Validators.required, this.atLeastOneAnimalTypeValidator()],
      ],
    });

    // In edit mode, email is already checked
    if (isEditMode) {
      this.emailChecked = true;
    }

    // Check if user is multi-company (only in edit mode)
    if (isEditMode && this.user) {
      // TODO: We need to know if user belongs to multiple companies
      // For now, we'll check if current user is admin to allow editing
      const currentUser = this.authService.getUser();
      this.isMultiCompanyUser = false; // We don't have this info yet, will be added later
    }

    // Disable fields based on context
    this.updateFieldsState();
  }

  private updateFieldsState(): void {
    const isEditMode = !!this.user;
    const currentUser = this.authService.getUser();
    const isAdmin = currentUser?.isAdmin || false;

    if (isEditMode && this.isMultiCompanyUser && !isAdmin) {
      // Disable personal info for multi-company users (except for admin)
      this.editForm.get("firstname")?.disable();
      this.editForm.get("lastname")?.disable();
      this.editForm.get("phone")?.disable();
    }

    // Role modification rules
    if (isEditMode) {
      if (this.context === "admin" && this.user?.isInternalRole()) {
        // Admin can change role for internal users (breeder <-> breederadmin)
        this.editForm.get("isBreederAdmin")?.enable();
      } else if (this.context === "breederadmin") {
        if (this.user?.role === "breederadmin") {
          // Breederadmin cannot change role of another breederadmin
          this.editForm.get("isExternal")?.disable();
          this.editForm.get("externalRole")?.disable();
        } else {
          // Breederadmin can change role of breeder and external users
          this.editForm.get("isExternal")?.enable();
        }
      } else {
        // Otherwise, role cannot be changed in edit mode
        this.editForm.get("isBreederAdmin")?.disable();
        this.editForm.get("isExternal")?.disable();
        this.editForm.get("externalRole")?.disable();
      }
    }
  }

  public onEmailBlur(): void {
    const email = this.editForm.get("email")?.value;
    if (!email || this.user) return; // Skip if edit mode

    // Check if email is valid before checking
    const emailControl = this.editForm.get("email");
    if (emailControl?.invalid) return;

    this.checkingEmail = true;
    this.errorMessage = null;
    this.isQwintalAdmin = false;

    this.userService.checkEmail(email).subscribe(
      (response) => {
        this.checkingEmail = false;
        this.emailChecked = true;
        this.emailExists = response.exists;

        if (response.exists && response.user) {
          // Check if user is Qwintal admin
          if (response.user.isAdmin) {
            this.isQwintalAdmin = true;
            this.errorMessage =
              "Cet utilisateur est un administrateur Qwintal et ne peut pas être ajouté";
            this.emailChecked = false; // Prevent form submission
            return;
          }

          // Pre-fill and disable fields
          this.editForm.patchValue({
            firstname: response.user.firstname,
            lastname: response.user.lastname,
            phone: response.user.phone,
          });
          this.editForm.get("firstname")?.disable();
          this.editForm.get("lastname")?.disable();
          this.editForm.get("phone")?.disable();
        } else {
          // Enable fields for new user
          this.editForm.get("firstname")?.enable();
          this.editForm.get("lastname")?.enable();
          this.editForm.get("phone")?.enable();
        }

        // Enable role selection after email verification (always enabled after check)
        this.editForm.get("isBreederAdmin")?.enable();
        this.editForm.get("isExternal")?.enable();
        this.applyAdminRoleExclusion();
      },
      (error) => {
        this.checkingEmail = false;
        this.errorMessage = "Erreur lors de la vérification de l'email";
      },
    );
  }

  public onExternalCheckboxChange(): void {
    const isExternal = this.editForm.get("isExternal")?.value;
    if (isExternal) {
      this.editForm.get("externalRole")?.setValidators(Validators.required);
    } else {
      this.editForm.get("externalRole")?.clearValidators();
      this.editForm.get("externalRole")?.setValue(null);
    }
    this.editForm.get("externalRole")?.updateValueAndValidity();
    this.applyAdminRoleExclusion();
  }

  public onBreederAdminCheckboxChange(): void {
    this.applyAdminRoleExclusion();
  }

  // Le breederadmin choisit un rôle externe en création comme en modification,
  // l'admin Qwintal en création seulement
  public get canChooseExternalRole(): boolean {
    if (this.context === "admin") {
      return !this.user;
    }
    return !this.user || this.user.role !== "breederadmin";
  }

  // Admin Qwintal en création : administrateur du compte client et utilisateur
  // externe s'excluent, cocher l'une des cases verrouille l'autre
  private applyAdminRoleExclusion(): void {
    if (this.context !== "admin" || this.user) return;

    const isBreederAdmin = this.editForm.get("isBreederAdmin")!;
    const isExternal = this.editForm.get("isExternal")!;
    if (isBreederAdmin.value) {
      isExternal.disable();
    } else {
      isExternal.enable();
    }
    if (isExternal.value) {
      isBreederAdmin.disable();
    } else {
      isBreederAdmin.enable();
    }
  }

  private getRoleFromForm(): UserCompanyRole {
    if (this.context === "admin") {
      if (this.canChooseExternalRole && this.editForm.get("isExternal")?.value) {
        return this.editForm.get("externalRole")?.value as UserCompanyRole;
      }
      const isBreederAdmin = this.editForm.get("isBreederAdmin")?.value;
      return isBreederAdmin ? UserCompanyRole.BREEDER_ADMIN : UserCompanyRole.BREEDER;
    } else {
      const isExternal = this.editForm.get("isExternal")?.value;
      if (isExternal) {
        const externalRole = this.editForm.get("externalRole")?.value;
        return externalRole as UserCompanyRole;
      }
      return UserCompanyRole.BREEDER;
    }
  }

  public saveAction(): void {
    if (!this.editForm.valid || !this.companyId) return;

    this.loading = true;
    this.errorMessage = null;

    const formValue = this.editForm.getRawValue();

    if (this.user) {
      // Update mode - separate info update and role update
      const role = this.getRoleFromForm();
      const hasRoleChanged = role !== this.user.role;

      // Always update personal info (firstname, lastname, phone)
      const infoValues = {
        firstname: formValue.firstname,
        lastname: formValue.lastname,
        phone: formValue.phone,
      };

      this.userService.updateUserInfo(this.user.id, infoValues).subscribe(
        (success) => {
          if (!success) {
            this.loading = false;
            this.errorMessage = "Erreur lors de la mise à jour des informations";
            return;
          }

          // Check if animal types changed
          const animalTypeIds = formValue.animalTypeIds;
          const hasAnimalTypesChanged =
            JSON.stringify(animalTypeIds.sort()) !==
            JSON.stringify(this.user!.animalTypeIds.sort());

          // If animal types changed, update them
          if (hasAnimalTypesChanged) {
            this.userService
              .updateUserAnimalTypes(this.companyId, this.user!.id, animalTypeIds)
              .subscribe(
              (success) => {
                if (!success) {
                  this.loading = false;
                  this.errorMessage = "Erreur lors de la mise à jour des types";
                  return;
                }

                // Continue with role update if needed
                this.updateRoleIfNeeded(hasRoleChanged, role);
              },
              (error) => {
                this.loading = false;
                if (error.status === 400) {
                  this.errorMessage = "Types d'animaux non valides";
                } else if (error.status === 403) {
                  this.errorMessage = "Au moins un type requis";
                } else {
                  this.errorMessage = "Erreur lors de la mise à jour des types";
                }
              },
            );
          } else {
            // Continue with role update if needed
            this.updateRoleIfNeeded(hasRoleChanged, role);
          }
        },
        (error) => {
          this.loading = false;
          this.errorMessage = "Erreur lors de la mise à jour des informations";
        },
      );
    } else {
      // Create mode
      const values = {
        email: formValue.email,
        firstname: formValue.firstname,
        lastname: formValue.lastname,
        phone: formValue.phone,
        role: this.getRoleFromForm(),
        animalTypeIds: formValue.animalTypeIds,
      };

      this.userService.addCompanyUser(values, this.companyId).subscribe(
        (user) => {
          this.loading = false;
          if (!user) {
            this.errorMessage = "Erreur lors de la création de l'utilisateur";
          } else {
            this.toastrService.show("", `Une invitation a été envoyée à ${user.email}`, {
              status: "success",
            });
            this.dialogRef.close();
          }
        },
        (error) => {
          this.loading = false;
          if (error.status === 400) {
            this.errorMessage = "Les types d'animaux sélectionnés ne sont pas valides";
          } else if (error.status === 403) {
            this.errorMessage = "L'utilisateur doit avoir au moins un type d'animal";
          } else {
            this.errorMessage = "Erreur lors de la création de l'utilisateur";
          }
        },
      );
    }
  }

  private updateRoleIfNeeded(hasRoleChanged: boolean, role: UserCompanyRole): void {
    // If role changed, update role separately
    // Admin can change breeder <-> breederadmin
    // Breederadmin can change breeder <-> external, but not breederadmin
    const canUpdateRole =
      (this.context === "admin" && this.user!.isInternalRole()) ||
      (this.context === "breederadmin" && this.user!.role !== "breederadmin");

    if (hasRoleChanged && canUpdateRole) {
      this.userService.updateCompanyUserRole(this.companyId, this.user!.id, role).subscribe(
        (user) => {
          this.loading = false;
          if (!user) {
            this.errorMessage = "Erreur lors de la mise à jour du rôle";
          } else {
            this.dialogRef.close();
          }
        },
        (error) => {
          this.loading = false;
          this.errorMessage = "Erreur lors de la mise à jour du rôle";
        },
      );
    } else {
      this.loading = false;
      this.dialogRef.close();
    }
  }

  public closeAction(): void {
    this.dialogRef.close();
  }
}
